import { HttpApiConfig } from "../http-api-config.js";
import { requestToSnakeCase } from "./axios-api-request-interceptor.js";
import { responseToCamelCase } from "./axios-api-response-interceptor.js";
//#region src/infrastructure/http-api/axios-api-client.ts
/** Pulls the filename out of a Content-Disposition header, handling both the
*  quoted `filename="x.xlsx"` form the DMS backend sends and the RFC 5987
*  `filename*=UTF-8''x.xlsx` form, which wins when both are present because it
*  is the one that can carry non-ASCII. Undefined when there is no name to
*  find, which leaves naming to the caller rather than inventing one. */
var filenameFromContentDisposition = (header) => {
	if (!header) return void 0;
	const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
	if (extended) try {
		return decodeURIComponent(extended[1].trim());
	} catch {}
	const name = (/filename\s*=\s*"([^"]*)"/.exec(header) ?? /filename\s*=\s*([^;]+)/.exec(header))?.[1]?.trim();
	return name ? name : void 0;
};
var AxiosApiClient = class {
	axiosClient;
	additionalHeadersProvider;
	constructor(axios, additionalHeadersProvider) {
		this.additionalHeadersProvider = additionalHeadersProvider;
		this.axiosClient = axios;
		this.axiosClient.interceptors.request.use(requestToSnakeCase);
		this.axiosClient.interceptors.response.use((response) => {
			return responseToCamelCase(response);
		});
	}
	async execute(method, url, params) {
		const body = typeof params === "object" && params !== null && "body" in params ? params.body : void 0;
		const isFile = body instanceof File || body instanceof Blob;
		const isFormData = typeof FormData !== "undefined" && params instanceof FormData;
		const headers = { ...this.additionalHeadersProvider?.() };
		if (!isFile && !isFormData) headers["Content-Type"] = "application/json";
		return (await this.axiosClient.request({
			method,
			url,
			data: params,
			headers,
			...isFile || isFormData ? { timeout: HttpApiConfig.UploadTimeout } : {}
		})).data;
	}
	async get(url, _addAuthorizationToken = true) {
		return await this.execute("get", url, {});
	}
	/** Downloads a file. `responseType: "blob"` is what keeps axios from parsing
	*  the body, and the interceptors leave a binary payload alone — so the bytes
	*  the backend rendered are the bytes the caller gets. */
	async getBlob(url, _addAuthorizationToken = true) {
		const response = await this.axiosClient.request({
			method: "get",
			url,
			responseType: "blob",
			headers: { ...this.additionalHeadersProvider?.() },
			timeout: HttpApiConfig.UploadTimeout
		});
		const headers = {};
		const raw = response.headers;
		if (raw && typeof raw === "object") {
			for (const [key, value] of Object.entries(raw)) if (typeof value === "string") headers[key.toLowerCase()] = value;
		}
		return {
			blob: response.data,
			filename: filenameFromContentDisposition(headers["content-disposition"]),
			headers
		};
	}
	async delete(url, params, _addAuthorizationToken = true) {
		return await this.execute("delete", url, params ?? {});
	}
	async post(url, params, _addAuthorizationToken = true) {
		return await this.execute("post", url, params);
	}
	async patch(url, params, _addAuthorizationToken = true) {
		return await this.execute("patch", url, params);
	}
	async put(url, params, _addAuthorizationToken = true) {
		return await this.execute("put", url, params);
	}
};
//#endregion
export { AxiosApiClient, filenameFromContentDisposition };

//# sourceMappingURL=axios-api-client.js.map